import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "../lib/supabase";

export type SessionState =
  "loading" | "anonymous" | "authenticated" | "recovery" | "demo";

export function useSession() {
  const forceDemo =
    new URLSearchParams(window.location.search).get("demo") === "1";
  const recoveryRequested =
    new URLSearchParams(window.location.search).get("recovery") === "1";
  const [state, setState] = useState<SessionState>(
    forceDemo || !supabaseConfigured ? "demo" : "loading",
  );
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    if (forceDemo || !supabaseConfigured) return;
    let active = true;
    const client = supabase();
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setState(
        data.session
          ? recoveryRequested
            ? "recovery"
            : "authenticated"
          : "anonymous",
      );
    });
    const { data } = client.auth.onAuthStateChange((event, next) => {
      if (!active) return;
      setSession(next);
      setState(
        event === "PASSWORD_RECOVERY" || (next && recoveryRequested)
          ? "recovery"
          : next
            ? "authenticated"
            : "anonymous",
      );
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [forceDemo, recoveryRequested]);

  const signOut = useCallback(async () => {
    if (!supabaseConfigured) return;
    await supabase().auth.signOut();
  }, []);

  return { state, session, signOut, isDemo: state === "demo" };
}
